/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:12:41 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { TextFormInput, /*ImageUploadFormInput,*/ RadioFormInput, OSAPIForm } from "ui-widgets/forms";
import { ProcessDefinition, PROCESSSTATUS } from "lib/schemas";
import { SingleDepartmentSelectInput } from "organizations/components/DeparmentSelectorInputs";
import { SinglePortalSelectInput } from "portals/components/PortalSelectorInputs";
import { StyledOSDialog } from "ui-widgets";
import { Info } from "react-feather";
import { COLORSCHEME } from "theme/styles";
import { useNavigate } from "react-router";
import { useProcessDefinitionsAPI } from "service-builder/services";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { useWatch } from "react-hook-form";
import { convertTextToInitials } from "lib/utils";
import { useServiceBuilderContext } from "service-builder/providers";
import { setCurrentProcessDefinition } from "service-builder/providers/actionCreators";
import { SERVICE_BUILDER_RELATIVE_URLS } from "service-builder/routes";
import { useRouteMatch, resolveURLName } from "core/routes/utils";
import { FromMediaLibraryFormInput } from "media-library/components/FromMediaLibraryFormInput";
import { TranslatableTextFormInput, TranslatableTextAreaInput } from "translation/components";
import { ProcessCategoryPicker } from "./ProcessCategoryPicker";

/**
 * ProcessDefinitionGeneralInfoForm: The actual component
 */

interface ProcessDefinitionGeneralInfoFormProps {
  closeProcessDefinitionGeneralInfoForm: () => void;
  processDefinitionGeneralInfoFormOpenState: boolean;
  processDefinition: ProcessDefinition;
}

export const ProcessDefinitionGeneralInfoForm = ({
  closeProcessDefinitionGeneralInfoForm,
  processDefinitionGeneralInfoFormOpenState,
  processDefinition,
}: ProcessDefinitionGeneralInfoFormProps) => {
  const navigate = useNavigate();

  const { dispatchServiceBuilderAction } = useServiceBuilderContext();

  const routeMatch = useRouteMatch(Object.values(SERVICE_BUILDER_RELATIVE_URLS));

  // This popup form is being used both within the service builder and outside (Services list page). So
  // we only save to context if dispatchServiceBuilderAction is available, otherwise, we just redirect to
  // the newly created process definition and the state will be dispatched there.
  const handleAPISaveProcessDefinition = React.useCallback(
    (processDefinition: ProcessDefinition) => {
      if (dispatchServiceBuilderAction) {
        dispatchServiceBuilderAction(setCurrentProcessDefinition(processDefinition));
      }

      const workSpaceURLs = Object.values(SERVICE_BUILDER_RELATIVE_URLS).filter(
        (url) => url !== SERVICE_BUILDER_RELATIVE_URLS.services
      );

      if (processDefinition?.id) {
        if (!workSpaceURLs.includes(routeMatch?.pattern?.path as any)) {
          navigate(resolveURLName("serviceBuilderRoot", { serviceId: processDefinition.id })); //Redirect for create mode only
        }
        closeProcessDefinitionGeneralInfoForm();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatchServiceBuilderAction, closeProcessDefinitionGeneralInfoForm, routeMatch]
  );

  const { useProcessDefinitionAPIForm } = useProcessDefinitionsAPI();

  const { useAPIFormReturn } = useProcessDefinitionAPIForm(processDefinition, handleAPISaveProcessDefinition);

  return (
    <StyledOSDialog
      popUpIcon={<Info color={COLORSCHEME.primaryVariant} />}
      popUpTitle="General Details"
      open={processDefinitionGeneralInfoFormOpenState}
      onClose={closeProcessDefinitionGeneralInfoForm}
      popUpDescription="Provide general information for the service."
    >
      <OSAPIForm useAPIFormReturn={useAPIFormReturn} loaderType="form-skeleton">
        <GeneralInfoFields />
      </OSAPIForm>
    </StyledOSDialog>
  );
};

const GeneralInfoFields = () => {
  const { control, getFieldState, resetField } = useOSFormContext<ProcessDefinition>();

  const [watchedServiceCode, organizationId, siteId] = useWatch({
    control,
    name: ["service_code", "artifact_org", "site"],
  });

  const serviceCodeIsDirty = getFieldState("service_code").isDirty;

  const onBlurTitle = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.value && !watchedServiceCode && !serviceCodeIsDirty) {
      resetField("service_code", { defaultValue: convertTextToInitials(event.target.value).join("") });
    }
  };

  return (
    <>
      <TranslatableTextFormInput<ProcessDefinition> onBlur={onBlurTitle} schema_field_key="title" label="Title" />
      <TextFormInput<ProcessDefinition>
        InputLabelProps={{
          shrink: Boolean(watchedServiceCode),
        }} /** Always shrink the label when a sequence value exists even when set by resetField. This is so that the label does not overlap with the auto created value that the MUI input field wont immediately detect. https://mui.com/api/input-label/ , https://stackoverflow.com/questions/50955603/react-material-ui-label-overlaps-with-text*/
        schema_field_key="service_code"
        label="Service Code"
      />
      <SinglePortalSelectInput<ProcessDefinition>
        schema_field_key="site"
        label="Portal"
        textFieldProps={{ placeholder: "Portal where the service will be accessible to your staff and/or clients." }}
      />
      <TranslatableTextAreaInput<ProcessDefinition>
        schema_field_key="description"
        label="Service Description"
        placeholder="This will be displayed to users of your service when they apply. Make sure it is descriptive and targeted at your end users."
      />
      <RadioFormInput<ProcessDefinition> schema_field_key="status" label="Status" options={PROCESSSTATUS} />
      {/* <ImageUploadFormInput<ProcessDefinition> schema_field_key="service_logo" label="Service Logo" /> */}
      <FromMediaLibraryFormInput<ProcessDefinition> schema_field_key="service_logo" label="Service Logo" />
      <SingleDepartmentSelectInput<ProcessDefinition> schema_field_key="department" label="Responsible Department" />
      <ProcessCategoryPicker<ProcessDefinition>
        schema_field_key="category"
        label="Category"
        organizationId={organizationId}
        site={siteId}
      />
    </>
  );
};
